import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowRight, Box } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/m/$moduleKey/')({
  component: CustomModuleHome,
})

function CustomModuleHome() {
  const { moduleKey } = Route.useParams()
  const t = useTranslations()

  const moduleQ = useQuery(
    orpc.modules.custom.modules.get.queryOptions({ input: { key: moduleKey } }),
  )
  // Expliciete bovengrens: de lijstprocedure pagineert serverzijde en dit scherm
  // toont elke entiteit van de module als tegel.
  const entitiesQ = useQuery(
    orpc.modules.custom.entities.list.queryOptions({ input: { moduleKey, limit: 500 } }),
  )

  const entities = entitiesQ.data?.rows ?? []
  const countQueries = useQueries({
    queries: entities.map((entity) => ({
      ...orpc.modules.custom.records.list.queryOptions({
        input: { moduleKey, entityKey: entity.key, page: 1, pageSize: 1 },
      }),
      staleTime: 60_000,
    })),
  })

  if (moduleQ.isLoading || entitiesQ.isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={`tile-${i}`} className="h-28" />
          ))}
        </div>
      </div>
    )
  }

  if (!moduleQ.data) {
    return (
      <div className="p-6">
        <PageHeader title="Module not found" subtitle={`No module with key '${moduleKey}'.`} />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={moduleQ.data.name} subtitle={moduleQ.data.description ?? undefined} />

      {entities.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center gap-2">
              <Box className="h-10 w-10 text-muted-foreground" />
              <p className="text-sm font-medium">{t('entityRuntime.moduleHome.noEntities')}</p>
              <p className="text-sm text-muted-foreground">
                {t('entityRuntime.moduleHome.noEntitiesHint')}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('entityRuntime.moduleHome.entitiesHeading')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {entities.map((entity, i) => {
              const countQ = countQueries[i]
              const total =
                countQ?.data && typeof countQ.data === 'object' && 'pagination' in countQ.data
                  ? (countQ.data as { pagination: { total: number } }).pagination.total
                  : null
              return (
                <Link
                  key={entity.id}
                  to="/m/$moduleKey/$entityKey"
                  params={{ moduleKey, entityKey: entity.key }}
                >
                  <Card className="hover:bg-muted/50 transition-colors cursor-pointer h-full">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span>{entity.namePlural ?? entity.name}</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {entity.description ? (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                          {entity.description}
                        </p>
                      ) : null}
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {countQ?.isLoading
                          ? '…'
                          : total === null
                            ? '—'
                            : t('entityRuntime.moduleHome.rowCount', { count: total })}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
